/**
 * Upload-ticket DTOs.
 *
 * Flow: widget asks `POST /v1/uploads/ticket` for a short-TTL HMAC ticket, then
 * PUT/POSTs the blob to the returned `uploadUrl` with the `fields`/headers. The
 * local StorageProvider validates the HMAC and writes to `.storage/`; a cloud
 * impl returns a real signed URL instead. Dashboard reads attachments via a
 * separate short-TTL ticketed GET.
 */
import type { z } from 'zod';
import { enum as zEnum, iso, number, object, record, string } from 'zod';

import { AttachmentKindSchema } from './enums.js';

/** Request a short-TTL upload ticket for one captured blob. */
export const UploadTicketRequestSchema = object({
  publicKey: string().min(1),
  kind: AttachmentKindSchema,
  contentType: string().min(1).max(256),
  /** Declared blob size in bytes (server enforces a cap). */
  sizeBytes: number().int().positive(),
  /**
   * The FeedbackSession to attach this blob to: the `id` returned by
   * `POST /v1/feedback`. The session must ALREADY exist (and belong to the same
   * org as the key) when the ticket is redeemed — the upload route links the
   * blob to it then. If omitted, or if no matching in-org session exists, the
   * blob is stored but left unlinked; there is no deferred re-attachment. So
   * always create the feedback first, then upload. (Named `clientSessionId` for
   * historical reasons; it carries the server-issued session id.)
   */
  clientSessionId: string().min(1).max(128).optional(),
});
export type UploadTicketRequest = z.infer<typeof UploadTicketRequestSchema>;

/**
 * The signed ticket. `uploadUrl` is where the widget sends the blob; `fields`
 * are extra form fields (cloud signed-POST) or headers the client must echo.
 * `token` is the opaque HMAC the backend validates on the upload route.
 */
export const UploadTicketResponseSchema = object({
  /** Storage key the blob will live under. */
  storagePath: string().min(1),
  /** Absolute or relative URL the client uploads to. */
  uploadUrl: string().min(1).max(4096),
  /** HTTP method to use for the upload. */
  method: zEnum(['PUT', 'POST']).default('PUT'),
  /** Opaque signed token bound to (storagePath, kind, contentType, size, exp). */
  token: string().min(1),
  /** Extra fields/headers to include with the upload. */
  fields: record(string(), string()).default({}),
  /**
   * Max accepted size in bytes for THIS ticket: the declared `sizeBytes`,
   * clamped to the deployment cap. It is what the upload route enforces, so a
   * client cannot declare a small blob and then send a large one.
   */
  maxBytes: number().int().positive(),
  /** ISO-8601 expiry of the ticket. */
  expiresAt: iso.datetime(),
});
export type UploadTicketResponse = z.infer<typeof UploadTicketResponseSchema>;

/**
 * Result of a completed/validated upload (returned by the upload route, and used
 * by ingestion to attach the blob to a session).
 */
export const UploadResultSchema = object({
  storagePath: string().min(1),
  kind: AttachmentKindSchema,
  contentType: string().min(1),
  sizeBytes: number().int().nonnegative(),
});
export type UploadResult = z.infer<typeof UploadResultSchema>;
